import type { SagaWorkflowGraphDto } from '@/dtos/SagaWorkflow'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { useEffect, useRef, useState } from 'react'
import { AppSettings } from '../appSettings/AppSettings'
import { useGetWorkflow } from './useErrorHandlingSagas'

// Dedicated saga-workflow hub (separate from the shared calc/import hub): a client connects only while a diagram is
// mounted and receives updates ONLY for the saga roots it subscribes to.
const SAGA_WORKFLOW_HUB_PATH = 'hub/saga-workflow'

/**
 * Streams a saga's live workflow graph: fetches the initial snapshot over REST, then keeps it current via a private
 * SignalR connection to the saga-workflow hub (subscribe on mount, re-subscribe + refetch on reconnect, unsubscribe +
 * stop on unmount). Full-snapshot replacement — last write wins.
 *
 * onUpdate fires whenever a live push (or reconnect refetch) advances the saga, letting the caller refresh sibling data
 * that shares the saga's lifecycle (e.g. the surrounding error-detail screen) so the whole page stays in sync, not just
 * the diagram.
 */
export const useSagaWorkflowStream = (rootId?: string, onUpdate?: () => void) => {
  const { data: snapshot, refetch, isLoading, isError } = useGetWorkflow(rootId)
  const [graph, setGraph] = useState<SagaWorkflowGraphDto | null>(null)

  // Keep refetch reachable from the connection callbacks without making it an effect dependency.
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  // Same for onUpdate: callers pass an inline closure, so ref it to avoid reopening the hub on every render.
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  // Seed local state from the REST snapshot; live pushes then replace it.
  useEffect(() => {
    if (snapshot) setGraph(snapshot)
  }, [snapshot])

  // A saga in a terminal state will never advance again, so there is nothing to stream — don't open the hub at all
  // (and tear it down if a live update transitions it to terminal). Gating on a derived boolean keeps the connection
  // stable across non-terminal pushes (the effect only re-runs when terminality flips).
  const shouldStream = !!graph && !graph.isTerminal

  useEffect(() => {
    if (!rootId || !shouldStream) return
    let unmounted = false

    const url = `${AppSettings.baseUrl}/${SAGA_WORKFLOW_HUB_PATH}`
    const conn = new HubConnectionBuilder()
      .configureLogging(LogLevel.Warning)
      .withUrl(url)
      .withAutomaticReconnect()
      .build()

    conn.on('SagaWorkflowUpdated', (g: SagaWorkflowGraphDto) => {
      // Guard against a stale push arriving mid-unsubscribe for a different root.
      if (g.rootSagaId !== rootId) return
      setGraph(g)
      onUpdateRef.current?.()
    })

    const subscribe = () => conn.invoke('SubscribeSaga', rootId).catch(() => { /* connection race — ignore */ })

    // Reconnect drops server-side group membership and connectionId → re-subscribe and refetch the authoritative
    // snapshot. Fire onUpdate only AFTER the refetch resolves, so the caller reacts to the fresh post-reconnect state.
    conn.onreconnected(async () => {
      subscribe()
      await refetchRef.current()
      onUpdateRef.current?.()
    })

    const start = async () => {
      try {
        await conn.start()
        if (unmounted) {
          conn.stop()
          return
        }
        await subscribe()
      } catch {
        // Connection failed (e.g. not logged in / offline). The diagram still shows the REST snapshot; no live updates.
      }
    }
    start()

    return () => {
      unmounted = true
      conn.invoke('UnsubscribeSaga', rootId).catch(() => { /* connection may already be closing */ })
      conn.stop()
    }
  }, [rootId, shouldStream])

  return { graph, isLoading, isError }
}
